import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  Typography,
  CircularProgress,
  Stack,
  IconButton,
  TextField,
  InputAdornment,
  Pagination,
} from "@mui/material";
import {
  FolderOpen,
  Visibility,
  Close,
  Search,
  Delete,
  AccessTime,
} from "@mui/icons-material";
import { Modal } from "@mui/material";
import { ModalBaseStyles, ModalHeader } from "../../Common/styles/modal";
import type { CohortDefinition } from "./types";
import http from "../../../utils/http";
import { debounce } from "lodash";
import { v4 as uuid } from "uuid";
import { pageSize } from "../../Common/styles/table";
import { AxiosResponse } from "axios";
import { errorToastMessage, toastMessage } from "../../../utils/toast";
import { DateTime } from "luxon";

interface SavedCohortsDialogProps {
  open: boolean;
  onClose: () => void;
  onLoadCohort: (cohort: CohortDefinition) => void;
}

const SavedCohortsDialog: React.FC<SavedCohortsDialogProps> = ({
  open,
  onClose,
  onLoadCohort,
}) => {
  const [searchText, setSearchText] = useState("");
  const [searchId, setSearchId] = useState<string>(uuid());
  const [isLoading, setIsLoading] = useState(false);
  const [loadingCohortId, setLoadingCohortId] = useState<string | null>(null);
  const [cohorts, setCohorts] = useState<CohortDefinition[]>([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [toggleLoader, setToggleLoader] = useState(false);

  const handleLoad = async (cohortId: string) => {
    try {
      setLoadingCohortId(cohortId);
      const res: AxiosResponse = await http.get(
        `/admin/registry/cohort-query/${cohortId}`
      );
      const cohortData = res.data?.data;
      onLoadCohort(cohortData);
      onClose();
    } catch (err) {
      errorToastMessage(err as Error);
    } finally {
      setLoadingCohortId(null);
    }
  };

  const handleDelete = async (cohortId: string) => {
    try {
      const res: AxiosResponse = await http.delete(
        `/admin/registry/cohort-query/${cohortId}`
      );
      toastMessage("success", res.data.message);
      setToggleLoader((prev) => !prev);
    } catch (err) {}
  };

  const handlePageChange = (
    event: React.ChangeEvent<unknown>,
    page: number
  ) => {
    setPage(page);
  };

  const modifySearchTerm = useMemo(
    () =>
      debounce((val) => {
        // Only trigger search if 3 or more characters
        if (val.length >= 3 || val.length === 0) {
          setSearchText(val);
          setPage(1);
        }
      }, 500),
    [setSearchText, setPage]
  );

  const handleClearSearch = () => {
    setSearchText("");
    setSearchId(uuid());
    setPage(1);
  };

  useEffect(() => {
    const fetchCohorts = async () => {
      try {
        setIsLoading(true);
        let url = `/admin/registry/cohort-query?size=${pageSize}&page=${page}`;

        if (searchText) {
          url += `&search=${searchText}`;
        }

        const res: AxiosResponse = await http.get(url);
        const data = res.data?.data;
        const count = data?.meta?.paginationOptions?.totalItems ?? 0;

        if (count > 0 && data?.data?.length === 0 && page > 1) {
          setPage(1);
          return;
        }

        const formattedData = data?.data?.map((item: any) => {
          let time = "";
          const createdAt = DateTime.fromISO(item?.updatedAt);
          let days = DateTime.now().diff(createdAt, "days").days;
          days = Math.floor(days);
          if (days <= 0) {
            let hours = DateTime.now().diff(createdAt, "hours").hours;
            hours = Math.floor(hours);
            time = hours ? `${hours} hour(s) ago` : "few mins ago";
          } else {
            time = `${days} day(s) ago`;
          }
          return {
            id: item?.id,
            name: item?.name,
            description: item?.description,
            time: "last updated " + time,
          };
        });

        setCohorts(formattedData || []);
        setTotalCount(count);
        setTotalPages(Math.ceil(count / pageSize));
      } catch (err) {
        errorToastMessage(err as Error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCohorts();
  }, [toggleLoader, page, searchText]);

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={{ ...ModalBaseStyles, maxWidth: "900px", minHeight: "auto" }}>
        <ModalHeader
          title="Saved Criteria Definitions"
          onCloseClick={onClose}
        />

        <Typography variant="body1" sx={{ mb: 3, color: "text.secondary" }}>
          View and load your previously saved criterias
        </Typography>

        {/* Search Bar */}
        <Box sx={{ mb: 3 }}>
          <TextField
            placeholder="Search criterias..."
            fullWidth
            defaultValue={searchText}
            onChange={(e) => modifySearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Search sx={{ color: "text.secondary" }} />
                </InputAdornment>
              ),
              endAdornment: searchText && (
                <InputAdornment position="end">
                  <IconButton onClick={handleClearSearch} size="small">
                    <Close />
                  </IconButton>
                </InputAdornment>
              ),
            }}
            key={searchId}
            sx={{
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
              },
            }}
          />
        </Box>

        {/* Results Count */}
        {!isLoading && (
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {totalCount} criteria{totalCount !== 1 ? "s" : ""} found
          </Typography>
        )}

        {/* Loading State */}
        {isLoading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress />
          </Box>
        )}

        {/* Empty State */}
        {cohorts.length === 0 && !isLoading && (
          <Box
            sx={{
              textAlign: "center",
              py: 6,
            }}
          >
            <Box
              sx={{
                width: 64,
                height: 64,
                bgcolor: "background.default",
                borderRadius: "50%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                mb: 2,
              }}
            >
              <FolderOpen sx={{ fontSize: 32, color: "text.secondary" }} />
            </Box>
            <Typography variant="h2" mb={1}>
              No Saved Criterias
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchText
                ? "No criterias match your search"
                : "Save your first criteria to see it here"}
            </Typography>
          </Box>
        )}

        {/* Cohort Cards */}
        {!isLoading && cohorts.length > 0 && (
          <>
            <Stack spacing={1.5} sx={{ mb: 3 }}>
              {cohorts.map((cohort) => (
                <Card
                  key={cohort.id}
                  sx={{
                    p: 1.5,
                    borderRadius: "12px",
                    border: "1px solid",
                    borderColor: "divider",
                    bgcolor: "background.paper",
                    boxShadow: "none",
                    transition: "all 0.2s ease-in-out",
                    "&:hover": {
                      borderColor: "primary.main",
                      boxShadow: "0px 2px 8px rgba(0, 0, 0, 0.1)",
                    },
                  }}
                  data-testid={`card-cohort-${cohort.id}`}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 2,
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        variant="subtitle1"
                        fontWeight={"medium"}
                        sx={{
                          fontSize: "0.95rem",
                          color: "text.primary",
                          mb: 0.25,
                          lineHeight: 1.3,
                        }}
                      >
                        {cohort.name}
                      </Typography>
                      {cohort.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{
                            fontSize: "0.8rem",
                            lineHeight: 1.3,
                            display: "-webkit-box",
                            WebkitLineClamp: 1,
                            WebkitBoxOrient: "vertical",
                            overflow: "hidden",
                            mb: 0.5,
                          }}
                        >
                          {cohort.description}
                        </Typography>
                      )}
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                      >
                        <AccessTime
                          sx={{ fontSize: 14, color: "text.secondary" }}
                        />
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontSize: "0.75rem" }}
                        >
                          {cohort.time}
                        </Typography>
                      </Box>
                    </Box>

                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => handleLoad(cohort.id)}
                        data-testid={`button-load-criteria-${cohort.id}`}
                        startIcon={
                          loadingCohortId === cohort.id ? (
                            <CircularProgress size={14} />
                          ) : (
                            <Visibility sx={{ fontSize: 14 }} />
                          )
                        }
                        disabled={loadingCohortId === cohort.id}
                        sx={{
                          borderRadius: "6px",
                          textTransform: "none",
                          fontWeight: 500,
                          fontSize: "0.8rem",
                          py: 0.5,
                          px: 1.5,
                        }}
                      >
                        {loadingCohortId === cohort.id ? "Loading..." : "Load"}
                      </Button>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(cohort.id)}
                        data-testid={`button-delete-criteria-${cohort.id}`}
                        sx={{
                          color: "text.secondary",
                          "&:hover": {
                            color: "error.main",
                          },
                        }}
                      >
                        <Delete sx={{ fontSize: 16 }} />
                      </IconButton>
                    </Box>
                  </Box>
                </Card>
              ))}
            </Stack>

            {/* Pagination */}
            {totalPages > 1 && (
              <Box sx={{ display: "flex", justifyContent: "center", mt: 3 }}>
                <Pagination
                  count={totalPages}
                  page={page}
                  onChange={handlePageChange}
                  color="primary"
                  size="small"
                />
              </Box>
            )}
          </>
        )}
      </Box>
    </Modal>
  );
};

export default SavedCohortsDialog;
